import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";

const FLAG_META = [
  { key: "card_issue", label: "Card issuance", hint: "Let customers issue new virtual cards." },
  { key: "topup", label: "Load funds", hint: "Allow Debit card / Bank account top-ups." },
  { key: "email_receiver", label: "Email receiver", hint: "Enable the Email receiver button." },
  { key: "sms_receiver", label: "Text card (MMS)", hint: "Enable the Text card button via Twilio." },
  { key: "refunds", label: "Refund notices", hint: "Allow customers to submit refund notices." },
];

export function ControlsTab({ flags, setFlags }) {
  const toggle = async (key, val) => {
    try {
      const r = await api.patch("/features", { flags: { [key]: val } });
      setFlags(r.data);
      toast.success(`${key} ${val ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Failed to update");
    }
  };
  return (
    <div className="space-y-6 max-w-3xl">
      <MasterMaintenanceCard />
      <ScheduledMaintenanceCard />
      <SignupsGateCard />
      <SigninGateCard />
      <div className="bg-white border border-[#E5E0D8] rounded-2xl p-8 space-y-5" data-testid="controls-panel">
        <div>
          <h3 className="serif text-2xl">Feature controls</h3>
          <p className="text-sm text-[#4A5C50]">Toggle customer-facing functions on or off globally.</p>
        </div>
        <div className="divide-y divide-[#E5E0D8]">
          {FLAG_META.map((f) => (
            <div key={f.key} className="flex items-center justify-between py-4" data-testid={`flag-${f.key}`}>
              <div className="flex-1 pr-6">
                <p className="font-medium">{f.label}</p>
                <p className="text-xs text-[#4A5C50] mt-0.5">{f.hint}</p>
              </div>
              <Switch checked={!!flags[f.key]} onCheckedChange={(v) => toggle(f.key, v)} data-testid={`flag-switch-${f.key}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MasterMaintenanceCard() {
  const [state, setState] = useState({ enabled: false, banner: "", loaded: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/maintenance-mode")
      .then((r) => setState({ ...r.data, loaded: true }))
      .catch(() => setState({ enabled: false, banner: "", loaded: true }));
  }, []);

  const save = async (next) => {
    setBusy(true);
    try {
      const r = await api.put("/admin/maintenance-mode", next);
      setState({ ...next, loaded: true, enabled: r.data.enabled });
      toast.success(next.enabled
        ? "Maintenance mode ON — both gates closed + banner live"
        : "Maintenance mode OFF — both gates re-opened");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`rounded-2xl p-8 space-y-5 border-2 ${
        state.enabled
          ? "bg-[#D96C4A]/5 border-[#D96C4A]/40"
          : "bg-white border-[#184A30]/30"
      }`}
      data-testid="maintenance-mode-panel"
    >
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Master switch</p>
          <h3 className="serif text-2xl mt-1">Maintenance mode</h3>
          <p className="text-sm text-[#4A5C50] mt-1">
            One-click freeze: pauses customer sign-ups <i>and</i> sign-ins, plus drops a sticky banner across the customer dashboard so logged-in users know what's going on. The two gates below remain editable for fine-grained control after the flip.
          </p>
        </div>
        <Switch
          checked={state.enabled}
          disabled={busy || !state.loaded}
          onCheckedChange={(v) => save({ enabled: v, banner: state.banner })}
          data-testid="maintenance-mode-switch"
        />
      </div>
      <div
        className={`rounded-xl px-4 py-2.5 text-xs ${
          state.enabled
            ? "bg-[#D96C4A]/15 text-[#B04E2F] border border-[#D96C4A]/30"
            : "bg-[#184A30]/5 text-[#184A30] border border-[#184A30]/20"
        }`}
        data-testid="maintenance-mode-status"
      >
        Maintenance mode is <b>{state.enabled ? "ON · platform frozen for customers" : "OFF · normal operation"}</b>
      </div>
      <div>
        <Label className="text-[11px] tracking-widest uppercase text-[#4A5C50]">
          Banner shown to logged-in customers (also used as the gate message)
        </Label>
        <textarea
          rows={3}
          value={state.banner}
          maxLength={400}
          onChange={(e) => setState({ ...state, banner: e.target.value.slice(0, 400) })}
          placeholder="e.g. Read-only maintenance window — back online by 4pm UTC."
          className="mt-1 w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#184A30]"
          data-testid="maintenance-mode-banner-input"
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-[11px] text-[#4A5C50]">
            {state.banner ? state.banner.length : 0}/400
          </p>
          <Button
            onClick={() => save({ enabled: state.enabled, banner: state.banner })}
            disabled={busy || !state.loaded}
            variant="outline"
            className="rounded-xl h-9"
            data-testid="maintenance-mode-save"
          >
            Save banner
          </Button>
        </div>
      </div>
    </div>
  );
}

function ScheduledMaintenanceCard() {
  const [state, setState] = useState({
    enabled: false,
    start_at: "",
    end_at: "",
    banner: "",
    notify_customers: false,
    notify_offset_minutes: 60,
    last_action: null,
    last_action_at: null,
    notify_sent_for: null,
    notify_recipients_count: null,
    loaded: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = () => api.get("/admin/maintenance-schedule")
      .then((r) => { if (mounted) setState((p) => ({
        ...p,
        ...r.data,
        start_at: r.data.start_at ? toLocalDtInput(r.data.start_at) : "",
        end_at: r.data.end_at ? toLocalDtInput(r.data.end_at) : "",
        loaded: true,
      })); })
      .catch(() => { if (mounted) setState((p) => ({ ...p, loaded: true })); });
    load();
    const t = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const save = async () => {
    if (state.enabled) {
      if (!state.start_at || !state.end_at) {
        toast.error("Pick both a start and an end time");
        return;
      }
      if (new Date(state.end_at) <= new Date(state.start_at)) {
        toast.error("End time must be after start time");
        return;
      }
    }
    setBusy(true);
    try {
      await api.put("/admin/maintenance-schedule", {
        enabled: state.enabled,
        start_at: state.enabled && state.start_at ? new Date(state.start_at).toISOString() : null,
        end_at: state.enabled && state.end_at ? new Date(state.end_at).toISOString() : null,
        banner: state.banner,
        notify_customers: !!state.notify_customers,
        notify_offset_minutes: Number(state.notify_offset_minutes) || 60,
      });
      toast.success(state.enabled ? "Maintenance window scheduled" : "Schedule cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const startD = state.start_at ? new Date(state.start_at) : null;
  const endD = state.end_at ? new Date(state.end_at) : null;
  const nowD = new Date();
  const upcoming = state.enabled && startD && startD > nowD;
  const inWindow = state.enabled && startD && endD && nowD >= startD && nowD < endD;

  return (
    <div
      className="rounded-2xl bg-white border border-[#E5E0D8] p-8 space-y-5"
      data-testid="maintenance-schedule-panel"
    >
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Auto-pilot</p>
          <h3 className="serif text-2xl mt-1">Scheduled maintenance window</h3>
          <p className="text-sm text-[#4A5C50] mt-1">
            Pre-set a future start/end (UTC). The platform auto-flips master maintenance mode ON at start and OFF at end — perfect for planned overnight deploys, no manual toggling needed.
          </p>
        </div>
        <Switch
          checked={state.enabled}
          disabled={busy || !state.loaded}
          onCheckedChange={(v) => setState({ ...state, enabled: v })}
          data-testid="maintenance-schedule-switch"
        />
      </div>

      {state.enabled && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-[#4A5C50]">Start (your local time)</Label>
              <Input
                type="datetime-local"
                value={state.start_at}
                onChange={(e) => setState({ ...state, start_at: e.target.value })}
                className="mt-1 mono"
                data-testid="maintenance-schedule-start"
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-[#4A5C50]">End (your local time)</Label>
              <Input
                type="datetime-local"
                value={state.end_at}
                onChange={(e) => setState({ ...state, end_at: e.target.value })}
                className="mt-1 mono"
                data-testid="maintenance-schedule-end"
              />
            </div>
          </div>

          <div>
            <Label className="text-[11px] tracking-widest uppercase text-[#4A5C50]">Banner copy (mirrored to gates + dashboard)</Label>
            <textarea
              rows={2}
              value={state.banner}
              maxLength={400}
              onChange={(e) => setState({ ...state, banner: e.target.value.slice(0, 400) })}
              placeholder="e.g. Planned deploy — back online by 4am UTC."
              className="mt-1 w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#184A30]"
              data-testid="maintenance-schedule-banner"
            />
          </div>

          <div className="rounded-xl border border-[#E5E0D8] p-4 bg-[#FBF9F6]" data-testid="maintenance-schedule-notify-block">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!state.notify_customers}
                onChange={(e) => setState({ ...state, notify_customers: e.target.checked })}
                className="mt-1"
                data-testid="maintenance-schedule-notify-toggle"
              />
              <div className="flex-1">
                <p className="text-sm text-[#112318] font-semibold">
                  Email all customers a heads-up before the window starts
                </p>
                <p className="text-xs text-[#4A5C50] mt-0.5">
                  Cuts down on "what's broken?" support tickets during planned deploys.
                  Sent once per scheduled window via Resend.
                </p>
                {state.notify_customers && (
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <span className="text-[#4A5C50]">Send</span>
                    <select
                      value={state.notify_offset_minutes}
                      onChange={(e) => setState({ ...state, notify_offset_minutes: Number(e.target.value) })}
                      className="rounded-md border border-[#E5E0D8] bg-white px-2 py-1 mono"
                      data-testid="maintenance-schedule-notify-offset"
                    >
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>1 hour</option>
                      <option value={120}>2 hours</option>
                      <option value={360}>6 hours</option>
                      <option value={1440}>24 hours</option>
                    </select>
                    <span className="text-[#4A5C50]">before start</span>
                  </div>
                )}
                {state.notify_sent_for && state.notify_recipients_count !== null && state.notify_sent_for === (state.start_at ? new Date(state.start_at).toISOString() : null) && (
                  <p className="text-[11px] text-[#184A30] mt-2" data-testid="maintenance-schedule-notify-sent">
                    ✓ Heads-up email sent to {state.notify_recipients_count} customer
                    {state.notify_recipients_count === 1 ? "" : "s"}
                    {state.notify_sent_at && ` · ${new Date(state.notify_sent_at).toLocaleString()}`}
                  </p>
                )}
              </div>
            </label>
          </div>
        </>
      )}

      <div
        className={`rounded-xl px-4 py-2.5 text-xs ${
          inWindow
            ? "bg-[#D96C4A]/15 text-[#B04E2F] border border-[#D96C4A]/30"
            : upcoming
            ? "bg-[#CBA35C]/15 text-[#8C6B21] border border-[#CBA35C]/30"
            : "bg-[#184A30]/5 text-[#184A30] border border-[#184A30]/20"
        }`}
        data-testid="maintenance-schedule-status"
      >
        {!state.enabled && "No window scheduled."}
        {state.enabled && upcoming && startD && (
          <>Window scheduled: <b>{startD.toLocaleString()}</b> → <b>{endD?.toLocaleString()}</b></>
        )}
        {state.enabled && inWindow && (
          <>Window in progress until <b>{endD?.toLocaleString()}</b></>
        )}
        {state.enabled && endD && nowD >= endD && (
          <>Window ended {endD.toLocaleString()} (auto-flipped OFF).</>
        )}
        {state.last_action && state.last_action_at && (
          <span className="block mt-1 opacity-70">
            Last scheduler action: {state.last_action} at {new Date(state.last_action_at).toLocaleString()}
          </span>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={busy || !state.loaded}
          className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
          data-testid="maintenance-schedule-save"
        >
          {busy ? "Saving…" : "Save schedule"}
        </Button>
      </div>
    </div>
  );
}

function toLocalDtInput(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function SignupsGateCard() {
  return (
    <GateCard
      title="Customer sign-ups"
      description="Pause new customer registrations during maintenance or while the site is under construction. Existing customers can still sign in and use the platform — only the registration form is gated."
      endpoint="/admin/signups-gate"
      testidPrefix="signups-gate"
      placeholder="e.g. We're upgrading our payment processor — back online by 6pm UTC. Thanks for your patience."
      onLabel="Sign-ups are OPEN"
      offLabel="Sign-ups are PAUSED"
      offHint="— the maintenance message below appears on the registration page."
      successOn="Sign-ups re-opened"
      successOff="Sign-ups paused for new customers"
    />
  );
}

function SigninGateCard() {
  return (
    <GateCard
      title="Customer sign-in"
      description="Pause customer logins (existing accounts) during maintenance. Admins and managers can ALWAYS sign in regardless — the platform owner can never be locked out by accident."
      endpoint="/admin/signin-gate"
      testidPrefix="signin-gate"
      placeholder="e.g. Brief maintenance window — please come back at 8pm UTC."
      onLabel="Sign-in is OPEN"
      offLabel="Customer sign-in is PAUSED"
      offHint="— customers see a maintenance banner on the sign-in page; admin/manager sign-ins still work."
      successOn="Customer sign-in re-opened"
      successOff="Customer sign-in paused"
    />
  );
}

function GateCard({ title, description, endpoint, testidPrefix, placeholder,
                    onLabel, offLabel, offHint, successOn, successOff }) {
  const [state, setState] = useState({ disabled: false, message: "", loaded: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(endpoint)
      .then((r) => setState({ ...r.data, loaded: true }))
      .catch(() => setState({ disabled: false, message: "", loaded: true }));
  }, [endpoint]);

  const save = async (next) => {
    setBusy(true);
    try {
      const r = await api.put(endpoint, next);
      setState({ ...next, loaded: true, disabled: r.data.disabled });
      toast.success(next.disabled ? successOff : successOn);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-8 space-y-5" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <h3 className="serif text-2xl">{title}</h3>
          <p className="text-sm text-[#4A5C50]">{description}</p>
        </div>
        <Switch
          checked={state.disabled}
          disabled={busy || !state.loaded}
          onCheckedChange={(v) => save({ disabled: v, message: state.message })}
          data-testid={`${testidPrefix}-switch`}
        />
      </div>
      <div
        className={`rounded-xl px-4 py-2.5 text-xs ${
          state.disabled
            ? "bg-[#CBA35C]/15 text-[#8C6B21] border border-[#CBA35C]/30"
            : "bg-[#184A30]/5 text-[#184A30] border border-[#184A30]/20"
        }`}
        data-testid={`${testidPrefix}-status`}
      >
        <b>{state.disabled ? offLabel : onLabel}</b>
        {state.disabled && ` ${offHint}`}
      </div>
      <div>
        <Label className="text-[11px] tracking-widest uppercase text-[#4A5C50]">
          Maintenance message (shown on the form)
        </Label>
        <textarea
          rows={3}
          value={state.message}
          maxLength={400}
          onChange={(e) => setState({ ...state, message: e.target.value.slice(0, 400) })}
          placeholder={placeholder}
          className="mt-1 w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#184A30]"
          data-testid={`${testidPrefix}-message-input`}
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-[11px] text-[#4A5C50]">
            {state.message ? state.message.length : 0}/400 — leave blank to use the default copy.
          </p>
          <Button
            onClick={() => save({ disabled: state.disabled, message: state.message })}
            disabled={busy || !state.loaded}
            variant="outline"
            className="rounded-xl h-9"
            data-testid={`${testidPrefix}-save-message`}
          >
            Save message
          </Button>
        </div>
      </div>
    </div>
  );
}
